import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Card, CardContent, Chip, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  Tabs, Tab, Autocomplete, FormControl, InputLabel, Select, MenuItem,
  Divider, IconButton, Tooltip, Snackbar,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Schedule as ScheduleIcon, CheckCircle as CheckIcon,
  LocationOn as LocationIcon, Phone as PhoneIcon,
  Payment as PaymentIcon, Add as AddIcon, Person as PersonIcon,
  Refresh as RefreshIcon, Today as TodayIcon,
  AccountBalanceWallet as WalletIcon, Engineering as TechIcon,
  Close as CloseIcon, WhatsApp as WhatsAppIcon, ContentCopy as CopyIcon,
  VpnKey as KeyIcon, Bolt as BoltIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { to12h } from '../../utils/helpers';

interface Appointment {
  _id: string;
  scheduledDate: string;
  timeSlot: string;
  status: string;
  technicianName: string;
  address: { street: string; city: string; coordinates?: { lat: number; lng: number } };
  leadName?: string;
  leadPhone?: string;
  mapsLink?: string;
  clientId?: { _id: string; name: string; email: string; phoneNumber?: string };
  deviceIds?: any[];
  notes?: string;
  clientNotes?: string;
  completionNotes?: string;
  completedAt?: string;
}

const statusConfig: Record<string, { label: string; color: any }> = {
  scheduled: { label: 'Programada', color: 'info' },
  in_progress: { label: 'En Progreso', color: 'warning' },
  completed: { label: 'Completada', color: 'success' },
  cancelled: { label: 'Cancelada', color: 'error' },
  no_show: { label: 'No Show', color: 'default' },
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('es-DO', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

// Normaliza un número dominicano a formato WhatsApp (1 + 10 dígitos), tolere como venga:
// pegado desde WhatsApp (+1 809-555-1234), todo junto (8095551234), con 00/+, espacios, etc.
function normalizeDoPhone(raw: string): string {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);      // prefijo internacional 00
  if (d.length === 10) return '1' + d;         // 8095551234 -> 18095551234
  if (d.length === 11 && d.startsWith('1')) return d; // ya en formato
  if (d.length > 11) {                          // exceso: tomar los últimos 10/11
    return d.startsWith('1') ? d.slice(-11) : '1' + d.slice(-10);
  }
  return d;                                     // fallback (dejar lo que haya)
}

// El técnico confundía "transferencia" con "no es mío". Se nombra a QUIÉN le llegó el
// dinero, porque eso es lo que decide si se lo debe a la empresa: si le transfieren a su
// cuenta, lo debe igual que si fuera efectivo.
type MetodoCobro = 'none' | 'cash' | 'transfer_technician' | 'transfer_company';

const OPCIONES_COBRO: { value: MetodoCobro; label: string; ayuda: string }[] = [
  { value: 'none', label: 'Sin pago / Pendiente', ayuda: 'El cliente paga después' },
  { value: 'cash', label: 'Efectivo a mí', ayuda: 'Me lo dieron en mano; se lo debo a Driports' },
  { value: 'transfer_technician', label: 'Transferencia a mí', ayuda: 'Cayó en MI cuenta; se lo debo a Driports igual que el efectivo' },
  { value: 'transfer_company', label: 'Transferencia a Driports', ayuda: 'Cayó en la cuenta de Driports; yo no lo tengo' },
];

const TechnicianDashboard: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('pending');
  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });

  // Close appointment dialog state
  const [closeDialog, setCloseDialog] = useState<{ open: boolean; appointment: Appointment | null; standalone?: boolean }>({ open: false, appointment: null });
  const [closing, setClosing] = useState(false);
  // Panel de credenciales tras cerrar (mostrar en pantalla + WhatsApp + reenviar)
  const [credResult, setCredResult] = useState<{ email: string; password: string; traccarUrl: string; clientName: string; clientId: string; clientPhone: string } | null>(null);
  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [clientTab, setClientTab] = useState(0);
  const [newClient, setNewClient] = useState({ name: '', email: '', phoneNumber: '', address: '', city: '' });
  const [closeData, setCloseData] = useState({
    clientId: '',
    completionNotes: '',
    deviceIds: [] as number[],
    deviceNames: [] as string[],
    paymentMethod: 'none' as MetodoCobro,
    paymentAmount: '',
    paymentReference: '',
  });

  // Devices and clients for close flow
  const [notInstalledDevices, setNotInstalledDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'pending' ? 'scheduled' : filter === 'completed' ? 'completed' : undefined;
      const [apptRes, statsRes] = await Promise.all([
        apiService.getMyAppointments(statusParam ? { status: statusParam } : {}),
        apiService.getMyStats(),
      ]);
      setAppointments(apptRes.data || []);
      setStats(statsRes.data);
    } catch (e: any) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadCloseResources = async () => {
    setCloseData({ clientId: '', completionNotes: '', deviceIds: [], deviceNames: [], paymentMethod: 'none', paymentAmount: '', paymentReference: '' });
    setNewClient({ name: '', email: '', phoneNumber: '', address: '', city: '' });
    setClientTab(0);

    setDevicesLoading(true);
    setClientsLoading(true);
    try {
      const [devRes, cliRes] = await Promise.all([
        apiService.getNotInstalledDevices(),
        apiService.getClients(1, 1000),
      ]);
      setNotInstalledDevices((devRes as any)?.data || []);
      const cliData = (cliRes as any)?.clients || (cliRes as any)?.data?.clients || (cliRes as any)?.data || [];
      setClients(Array.isArray(cliData) ? cliData : []);
    } catch (e) {
      console.error('Error loading devices/clients:', e);
    } finally {
      setDevicesLoading(false);
      setClientsLoading(false);
    }
  };

  const openCloseDialog = async (appointment: Appointment) => {
    setCloseDialog({ open: true, appointment, standalone: false });
    await loadCloseResources();
    // Prellenar el cliente con lo que ya trae la cita para no reingresar los datos.
    if (appointment.clientId?._id) {
      setClientTab(1);
      setCloseData(prev => ({ ...prev, clientId: appointment.clientId!._id }));
    } else if (appointment.leadName || appointment.leadPhone) {
      setClientTab(0);
      const street = appointment.address?.street && appointment.address.street !== 'Por definir' ? appointment.address.street : '';
      const city = appointment.address?.city && appointment.address.city !== 'N/A' ? appointment.address.city : '';
      setNewClient(prev => ({
        ...prev,
        name: appointment.leadName || '',
        phoneNumber: appointment.leadPhone || '',
        address: street,
        city,
      }));
    }
  };

  // Deep-link ?close=<id> (viene del WhatsApp que recibe el técnico): abre directo el
  // Clientes con precio pactado (dealers): el monto no se digita, se calcula solo con
  // el precio acordado por la cantidad de equipos. Evita errores de tecleo y discusiones
  // después. El backend vuelve a aplicar el mismo precio, esto es solo lo que se ve.
  const clienteSeleccionado = clients.find((c: any) => c._id === closeData.clientId);
  const precioPactado: number | null =
    clientTab === 1 && clienteSeleccionado?.installationPrice > 0
      ? clienteSeleccionado.installationPrice
      : null;

  useEffect(() => {
    if (!precioPactado) return;
    const equipos = Math.max(closeData.deviceIds.length, 1);
    const total = String(precioPactado * equipos);
    setCloseData(p => (p.paymentAmount === total ? p : { ...p, paymentAmount: total }));
  }, [precioPactado, closeData.deviceIds.length]);

  // Piso de precio: el mismo que valida el backend. Un dedo mal puesto (500 en vez de 5000)
  // descuadra el ingreso y el efectivo que se le carga al técnico, así que se avisa aquí
  // antes de mandar. El precio pactado del dealer lo puso el admin y no se cuestiona.
  const MONTO_MINIMO = 4500;
  const minimoRequerido = MONTO_MINIMO * Math.max(closeData.deviceIds.length, 1);
  const montoDigitado = parseFloat(closeData.paymentAmount) || 0;
  const montoBajoMinimo =
    closeData.paymentMethod !== 'none' && !precioPactado && montoDigitado < minimoRequerido;

  // diálogo de cierre de esa cita una vez cargada la lista. Se ejecuta una sola vez.
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  useEffect(() => {
    if (deepLinkHandled || loading) return;
    const closeId = new URLSearchParams(window.location.search).get('close');
    if (!closeId) return;
    const appt = appointments.find(a => a._id === closeId);
    if (appt) {
      setDeepLinkHandled(true);
      openCloseDialog(appt);
      window.history.replaceState({}, '', window.location.pathname); // no reabrir al refrescar
    } else if (!loading && appointments.length) {
      setDeepLinkHandled(true); // cargó pero no está en el filtro actual (ya cerrada u otro estado)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, loading, deepLinkHandled]);

  // Instalar sin cita previa
  const openStandaloneDialog = async () => {
    setCloseDialog({ open: true, appointment: null, standalone: true });
    await loadCloseResources();
  };

  const handleClose = () => {
    setCloseDialog({ open: false, appointment: null });
  };

  const handleSubmitClose = async () => {
    if (!closeDialog.appointment && !closeDialog.standalone) return;

    if (clientTab === 0 && (!newClient.name || !newClient.email || !newClient.phoneNumber)) {
      setAlert({ show: true, type: 'error', message: 'Nombre, email y teléfono son requeridos para crear el cliente' });
      return;
    }
    if (clientTab === 1 && !closeData.clientId) {
      setAlert({ show: true, type: 'error', message: 'Debe seleccionar un cliente existente' });
      return;
    }
    if (closeData.deviceIds.length === 0) {
      setAlert({ show: true, type: 'error', message: 'Debe seleccionar al menos un dispositivo' });
      return;
    }
    const emptyNames = closeData.deviceNames.filter(n => !n || !n.trim());
    if (emptyNames.length > 0) {
      setAlert({ show: true, type: 'error', message: 'Debe proporcionar nombres para todos los dispositivos' });
      return;
    }
    if (montoBajoMinimo) {
      setAlert({
        show: true,
        type: 'error',
        message: `El monto mínimo es RD$${MONTO_MINIMO.toLocaleString('es-DO')} por equipo (aquí RD$${minimoRequerido.toLocaleString('es-DO')}). Revisa lo que digitaste; si el cliente de verdad pagó menos, el admin tiene que registrarlo.`,
      });
      return;
    }

    setClosing(true);
    try {
      const payload: any = {
        completionNotes: closeData.completionNotes,
        deviceIds: closeData.deviceIds,
        deviceNames: closeData.deviceNames,
        paymentMethod: closeData.paymentMethod,
        paymentAmount: closeData.paymentAmount ? parseFloat(closeData.paymentAmount) : 0,
        paymentReference: closeData.paymentReference || undefined,
      };

      if (clientTab === 0) {
        payload.newClient = { ...newClient, phoneNumber: normalizeDoPhone(newClient.phoneNumber) };
      } else {
        payload.clientId = closeData.clientId;
      }

      const result = closeDialog.standalone
        ? await apiService.quickCloseInstallation(payload)
        : await apiService.closeAppointment(closeDialog.appointment!._id, payload);

      if (!result || !(result as any).success) {
        setAlert({ show: true, type: 'error', message: (result as any)?.message || 'Error cerrando la cita' });
        return;
      }

      const details = [];
      if ((result as any).data?.traccarUserCreated) details.push('Usuario Traccar creado');
      if ((result as any).data?.emailSent) details.push('Credenciales enviadas por email');
      const transfers = (result as any).data?.deviceTransfers?.filter((t: any) => t.status === 'success').length || 0;
      if (transfers) details.push(`${transfers} dispositivo(s) transferido(s)`);

      // Si se crearon credenciales, mostrarlas en pantalla para enviarlas por WhatsApp
      const creds = (result as any).data?.credentials;
      const cli = (result as any).data?.appointment?.clientId;
      if (creds?.password) {
        setCredResult({
          email: creds.email || '',
          password: creds.password || '',
          traccarUrl: creds.traccarUrl || '',
          clientName: (cli && cli.name) || newClient.name || '',
          clientId: (cli && cli._id) || cli || '',
          clientPhone: (cli && cli.phoneNumber) || newClient.phoneNumber || '',
        });
        setResendEmail(creds.email || '');
      }

      setAlert({ show: true, type: 'success', message: `Instalación registrada. ${details.join(', ')}` });
      handleClose();
      fetchData();
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || e.message || 'Error cerrando la cita' });
    } finally {
      setClosing(false);
    }
  };

  const pendingAppts = appointments.filter(a => a.status === 'scheduled' || a.status === 'in_progress');
  const todayAppts = pendingAppts.filter(a => {
    const d = new Date(a.scheduledDate);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  });

  // ── Panel de credenciales: mensaje, copiar, WhatsApp, reenviar ──
  const buildCredMessage = (c: { clientName: string; email: string; password: string; traccarUrl: string }) =>
    `¡Hola${c.clientName ? ' ' + c.clientName : ''}! 🚗 Tu GPS Driports ya está activo y transmitiendo en tiempo real.\n\n` +
    `Entra a la plataforma:\n🔗 ${c.traccarUrl}\n👤 Usuario: ${c.email}\n🔑 Contraseña: ${c.password}\n\n` +
    `📲 Descarga la app *Driports* y entra con esos mismos datos:\n` +
    `Android: https://play.google.com/store/apps/details?id=com.driports.com\n` +
    `iPhone: https://apps.apple.com/gr/app/driports/id6544789743\n\n` +
    `✉️ Estos datos también te llegaron por correo.\n` +
    `Por seguridad, te recomendamos cambiar la contraseña en tu primer acceso. ¡Bienvenido! 🙌`;

  const handleCopyCreds = async () => {
    if (!credResult) return;
    try {
      await navigator.clipboard.writeText(buildCredMessage(credResult));
      setAlert({ show: true, type: 'success', message: 'Credenciales copiadas' });
    } catch { /* clipboard no disponible */ }
  };

  const handleWhatsAppCreds = () => {
    if (!credResult) return;
    const waId = normalizeDoPhone(credResult.clientPhone || '');
    const text = encodeURIComponent(buildCredMessage(credResult));
    window.open(waId ? `https://wa.me/${waId}?text=${text}` : `https://wa.me/?text=${text}`, '_blank');
  };

  const handleResendCredentials = async () => {
    if (!credResult?.clientId) {
      setAlert({ show: true, type: 'error', message: 'No hay cliente asociado para reenviar' });
      return;
    }
    setResending(true);
    try {
      const res = await apiService.resendCredentials({ clientId: credResult.clientId, email: resendEmail || undefined });
      const data = (res as any)?.data;
      if ((res as any)?.success && data?.password) {
        setCredResult(prev => prev ? { ...prev, email: data.email, password: data.password } : prev);
        setAlert({ show: true, type: 'success', message: data.emailSent ? 'Nueva clave generada y enviada por email' : 'Nueva clave generada (envíala por WhatsApp)' });
      } else {
        setAlert({ show: true, type: 'error', message: (res as any)?.message || 'No se pudo regenerar' });
      }
    } catch (e: any) {
      setAlert({ show: true, type: 'error', message: e.response?.data?.message || e.message || 'Error regenerando credenciales' });
    } finally {
      setResending(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>Mi Portal</Typography>
          <Typography variant="body2" color="text.secondary">Gestiona tus citas e instalaciones</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button variant="contained" color="secondary" startIcon={<BoltIcon />} onClick={openStandaloneDialog}>
            Instalar sin cita
          </Button>
          <Tooltip title="Actualizar">
            <IconButton onClick={fetchData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Hoy', value: stats.today || 0, color: 'primary.main', icon: <TodayIcon /> },
            { label: 'Pendientes', value: stats.pending || 0, color: 'warning.main', icon: <ScheduleIcon /> },
            { label: 'Completadas (Mes)', value: stats.completedThisMonth || 0, color: 'success.main', icon: <CheckIcon /> },
            {
              // netBalance = comisiones − efectivo: negativo es lo que él tiene que entregar.
              label: (stats.balance?.netBalance || 0) > 0 ? 'Driports me debe' : 'Tengo que entregar',
              value: `${Math.abs(stats.balance?.netBalance || 0).toLocaleString('es-DO')} DOP`,
              color: (stats.balance?.netBalance || 0) < 0 ? 'error.main' : 'success.main',
              icon: <WalletIcon />,
            },
          ].map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Card>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ backgroundColor: s.color, color: 'white', borderRadius: 2, p: 1.5, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="h5" fontWeight={600} noWrap>{s.value}</Typography>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={filter} onChange={(_, v) => setFilter(v)}>
          <Tab label={`Pendientes (${pendingAppts.length})`} value="pending" />
          <Tab label="Completadas" value="completed" />
          <Tab label="Todas" value="all" />
        </Tabs>
      </Paper>

      {/* Today highlight */}
      {filter === 'pending' && todayAppts.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Tienes <strong>{todayAppts.length} cita(s)</strong> programada(s) para hoy
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : appointments.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TechIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No hay citas {filter === 'pending' ? 'pendientes' : filter === 'completed' ? 'completadas' : ''}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {appointments.map((appt) => {
            const cfg = statusConfig[appt.status] || { label: appt.status, color: 'default' };
            const isToday = todayAppts.some(t => t._id === appt._id);
            return (
              <Grid size={{ xs: 12, md: 6 }} key={appt._id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderLeft: isToday ? '4px solid' : undefined,
                    borderLeftColor: isToday ? 'primary.main' : undefined,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {formatDate(appt.scheduledDate)} - {to12h(appt.timeSlot)}
                        </Typography>
                        {isToday && <Chip label="HOY" size="small" color="primary" sx={{ ml: 1 }} />}
                      </Box>
                      <Chip label={cfg.label} color={cfg.color} size="small" />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2">{appt.address.street}, {appt.address.city}</Typography>
                    </Box>

                    {appt.clientId && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">{appt.clientId.name}</Typography>
                        {appt.clientId.phoneNumber && (
                          <>
                            <PhoneIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                            <Typography variant="body2">{appt.clientId.phoneNumber}</Typography>
                          </>
                        )}
                      </Box>
                    )}

                    {!appt.clientId && (appt.leadName || appt.leadPhone) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">{appt.leadName || 'Lead'}</Typography>
                        {appt.leadPhone && (
                          <>
                            <PhoneIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                            <Typography variant="body2">{appt.leadPhone}</Typography>
                          </>
                        )}
                      </Box>
                    )}

                    {(appt.mapsLink || appt.address?.coordinates) && (
                      <Button
                        size="small"
                        startIcon={<LocationIcon />}
                        href={appt.mapsLink || `https://www.google.com/maps?q=${appt.address!.coordinates!.lat},${appt.address!.coordinates!.lng}`}
                        target="_blank"
                        rel="noopener"
                        sx={{ mt: 0.5, textTransform: 'none' }}
                      >
                        Ver ubicación en el mapa
                      </Button>
                    )}

                    {(appt.clientId?.phoneNumber || appt.leadPhone) && (
                      <Button
                        size="small"
                        color="success"
                        startIcon={<WhatsAppIcon />}
                        href={`https://wa.me/${normalizeDoPhone(appt.clientId?.phoneNumber || appt.leadPhone || '')}`}
                        target="_blank"
                        rel="noopener"
                        sx={{ mt: 0.5, ml: 1, textTransform: 'none' }}
                      >
                        WhatsApp cliente
                      </Button>
                    )}

                    {appt.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {appt.notes}
                      </Typography>
                    )}

                    {appt.clientNotes && (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        <Typography variant="caption">{appt.clientNotes}</Typography>
                      </Alert>
                    )}

                    {(appt.status === 'scheduled' || appt.status === 'in_progress') && (
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        startIcon={<CheckIcon />}
                        onClick={() => openCloseDialog(appt)}
                      >
                        Cerrar Cita
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ===== CLOSE APPOINTMENT DIALOG ===== */}
      <Dialog open={closeDialog.open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {closeDialog.standalone ? 'Instalar sin cita — Completar Instalación' : 'Cerrar Cita y Completar Instalación'}
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {(closeDialog.appointment || closeDialog.standalone) && (
            <>
              {/* Appointment info (solo si viene de una cita) */}
              {closeDialog.appointment && (
                <Box sx={{ mb: 3, mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Fecha:</Typography>
                      <Typography>{formatDate(closeDialog.appointment.scheduledDate)} - {to12h(closeDialog.appointment.timeSlot)}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Dirección:</Typography>
                      <Typography>{closeDialog.appointment.address.street}, {closeDialog.appointment.address.city}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Alert severity="info" sx={{ mb: 3 }}>
                Al completar esta cita se creará el usuario Traccar, se transferirán los dispositivos al cliente y se enviarán las credenciales por email.
              </Alert>

              {/* Client selection */}
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <Tabs value={clientTab} onChange={(_, v) => setClientTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tab label="Crear Nuevo Cliente" icon={<AddIcon />} iconPosition="start" />
                  <Tab label="Seleccionar Existente" icon={<PersonIcon />} iconPosition="start" />
                </Tabs>
                <Box sx={{ p: 3 }}>
                  {clientTab === 0 && (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField fullWidth required label="Nombre Completo" value={newClient.name}
                          onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} placeholder="Juan Pérez" />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField fullWidth required label="Email" type="email" value={newClient.email}
                          onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} placeholder="cliente@email.com" />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField fullWidth required label="Teléfono (WhatsApp)" value={newClient.phoneNumber}
                          onChange={e => setNewClient(p => ({ ...p, phoneNumber: e.target.value }))} placeholder="809-555-1234"
                          helperText="Necesario para enviarle el acceso por WhatsApp" />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField fullWidth label="Ciudad" value={newClient.city}
                          onChange={e => setNewClient(p => ({ ...p, city: e.target.value }))} placeholder="Santo Domingo" />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth label="Dirección" value={newClient.address}
                          onChange={e => setNewClient(p => ({ ...p, address: e.target.value }))} placeholder="Calle Principal #123" />
                      </Grid>
                    </Grid>
                  )}
                  {clientTab === 1 && (
                    <Autocomplete
                      options={clients}
                      loading={clientsLoading}
                      getOptionLabel={(o: any) => `${o.name} - ${o.email}`}
                      value={clients.find((c: any) => c._id === closeData.clientId) || null}
                      onChange={(_, v) => setCloseData(p => ({ ...p, clientId: v?._id || '' }))}
                      renderInput={params => (
                        <TextField {...params} label="Buscar Cliente" placeholder="Nombre o email..." />
                      )}
                      renderOption={(props, o: any) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body1">{o.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{o.email}</Typography>
                          </Box>
                        </li>
                      )}
                    />
                  )}
                </Box>
              </Paper>

              {/* Device selection */}
              <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Dispositivos Instalados</Typography>
                <Autocomplete
                  multiple
                  options={notInstalledDevices}
                  loading={devicesLoading}
                  getOptionLabel={(o: any) => `${o.imei} - ${o.model || 'N/A'}`}
                  value={notInstalledDevices.filter((d: any) => closeData.deviceIds.includes(d.traccarId || d.id))}
                  onChange={(_, v) => {
                    const ids = v.map((d: any) => d.traccarId || d.id);
                    setCloseData(p => ({ ...p, deviceIds: ids, deviceNames: Array(ids.length).fill('') }));
                  }}
                  renderInput={params => (
                    <TextField {...params} label="Seleccionar Dispositivos" placeholder="Buscar por IMEI..."
                      error={closeData.deviceIds.length === 0}
                      helperText={closeData.deviceIds.length === 0 ? 'Seleccione al menos un dispositivo' : `${closeData.deviceIds.length} seleccionado(s)`} />
                  )}
                  renderOption={(props, o: any) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2">IMEI: {o.imei}</Typography>
                        <Typography variant="caption" color="text.secondary">{o.model || 'N/A'} - TraccarID: {o.traccarId || o.id}</Typography>
                      </Box>
                    </li>
                  )}
                />
              </Paper>

              {/* Device names */}
              {closeData.deviceIds.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Nombres de Dispositivos</Typography>
                  <Alert severity="warning" sx={{ mb: 2 }}>Cada dispositivo debe tener un nombre. Se configurará expiración de 1 año.</Alert>
                  <Grid container spacing={2}>
                    {closeData.deviceIds.map((tid, i) => {
                      const dev = notInstalledDevices.find((d: any) => (d.traccarId || d.id) === tid);
                      return (
                        <Grid key={i} size={{ xs: 12 }}>
                          <TextField fullWidth required
                            label={`Nombre Dispositivo ${i + 1} - IMEI: ${dev?.imei || 'N/A'}`}
                            value={closeData.deviceNames[i] || ''}
                            onChange={e => {
                              const names = [...closeData.deviceNames];
                              names[i] = e.target.value;
                              setCloseData(p => ({ ...p, deviceNames: names }));
                            }}
                            placeholder={`Ej: Camioneta ${dev?.model || ''}`}
                            helperText="Este nombre se mostrará en Traccar"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              )}

              {/* Payment */}
              <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PaymentIcon color="primary" /> Pago del Cliente
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>¿Quién recibió el dinero?</InputLabel>
                      <Select value={closeData.paymentMethod} label="¿Quién recibió el dinero?"
                        onChange={e => setCloseData(p => ({ ...p, paymentMethod: e.target.value as MetodoCobro }))}>
                        {OPCIONES_COBRO.map(o => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {OPCIONES_COBRO.find(o => o.value === closeData.paymentMethod)?.ayuda}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth size="small" label="Monto (DOP)" type="number"
                      value={closeData.paymentAmount}
                      onChange={e => setCloseData(p => ({ ...p, paymentAmount: e.target.value }))}
                      disabled={closeData.paymentMethod === 'none' || !!precioPactado}
                      helperText={precioPactado ? 'Precio acordado con el cliente' : undefined} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField fullWidth size="small" label="Referencia (opcional)"
                      value={closeData.paymentReference}
                      onChange={e => setCloseData(p => ({ ...p, paymentReference: e.target.value }))}
                      disabled={closeData.paymentMethod === 'none'} />
                  </Grid>
                </Grid>
                {precioPactado && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <strong>{clienteSeleccionado?.name}</strong> tiene precio acordado de{' '}
                    <strong>{precioPactado.toLocaleString('es-DO')} DOP</strong> por equipo.
                    {closeData.deviceIds.length > 1 && ` ${closeData.deviceIds.length} equipos = ${(precioPactado * closeData.deviceIds.length).toLocaleString('es-DO')} DOP.`}
                    {' '}El monto se llena solo, no hay que digitarlo.
                  </Alert>
                )}
                {montoBajoMinimo && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    El monto mínimo es <strong>RD${MONTO_MINIMO.toLocaleString('es-DO')}</strong> por equipo
                    {closeData.deviceIds.length > 1 && ` (${closeData.deviceIds.length} equipos = RD$${minimoRequerido.toLocaleString('es-DO')})`}.
                    Revisa lo que digitaste; si el cliente de verdad pagó menos, el admin tiene que registrarlo.
                  </Alert>
                )}
                {!montoBajoMinimo && closeData.paymentMethod !== 'none' && closeData.paymentAmount && (
                  <Alert
                    severity={closeData.paymentMethod === 'transfer_company' ? 'success' : 'info'}
                    sx={{ mt: 1 }}
                  >
                    {closeData.paymentMethod === 'transfer_company' ? (
                      <>
                        <strong>{parseFloat(closeData.paymentAmount).toLocaleString('es-DO')} DOP</strong> van
                        directo a la cuenta de Driports. No se te suman a lo que tienes que entregar.
                      </>
                    ) : (
                      <>
                        <strong>{parseFloat(closeData.paymentAmount).toLocaleString('es-DO')} DOP</strong>{' '}
                        {closeData.paymentMethod === 'cash' ? 'en efectivo' : 'a tu cuenta'} — se te suman a lo
                        que tienes que entregar a Driports.
                      </>
                    )}
                  </Alert>
                )}
              </Paper>

              {/* Notes */}
              <TextField fullWidth label="Notas de Completación (opcional)" multiline rows={3}
                value={closeData.completionNotes}
                onChange={e => setCloseData(p => ({ ...p, completionNotes: e.target.value }))}
                placeholder="Comentarios sobre la instalación..." />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={closing}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmitClose}
            disabled={
              closing ||
              (clientTab === 0 && (!newClient.name || !newClient.email || !newClient.phoneNumber)) ||
              (clientTab === 1 && !closeData.clientId) ||
              closeData.deviceIds.length === 0 ||
              montoBajoMinimo
            }
            startIcon={closing ? <CircularProgress size={20} /> : <CheckIcon />}>
            {closing ? 'Procesando...' : 'Completar y Cerrar Cita'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Panel de credenciales del cliente (mostrar en pantalla + enviar por WhatsApp) */}
      <Dialog open={!!credResult} onClose={() => setCredResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon color="success" /> Acceso del cliente
        </DialogTitle>
        <DialogContent>
          {credResult && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Instalación registrada. <strong>Envíale estas credenciales al cliente ahora por WhatsApp</strong> — no dependas solo del correo.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Enlace</Typography>
                <Typography sx={{ mb: 1, wordBreak: 'break-all' }}>{credResult.traccarUrl}</Typography>
                <Typography variant="caption" color="text.secondary">Usuario</Typography>
                <Typography sx={{ mb: 1, fontFamily: 'monospace' }}>{credResult.email}</Typography>
                <Typography variant="caption" color="text.secondary">Contraseña</Typography>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 600 }}>{credResult.password}</Typography>
              </Paper>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button variant="contained" sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1da851' } }}
                  startIcon={<WhatsAppIcon />} onClick={handleWhatsAppCreds}>
                  Enviar por WhatsApp
                </Button>
                <Button variant="outlined" startIcon={<CopyIcon />} onClick={handleCopyCreds}>Copiar</Button>
              </Box>
              <Divider sx={{ my: 1 }}>¿Correo mal escrito o no llegó?</Divider>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <TextField size="small" label="Corregir correo (opcional)" type="email" value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
                <Button variant="text" onClick={handleResendCredentials} disabled={resending}
                  startIcon={resending ? <CircularProgress size={18} /> : <RefreshIcon />}>
                  {resending ? 'Generando...' : 'Regenerar y reenviar'}
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Regenerar crea una contraseña nueva (la anterior deja de servir) y actualiza el correo de acceso.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredResult(null)}>Listo</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar alerts */}
      <Snackbar open={alert.show} autoHideDuration={6000} onClose={() => setAlert(p => ({ ...p, show: false }))}>
        <Alert severity={alert.type} onClose={() => setAlert(p => ({ ...p, show: false }))} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnicianDashboard;
